/**
 * Mapping between Medplum/FHIR resources and the Service Request Tracker view model.
 *
 * The tracker renders `StageRow`s (which are a superset of legacy `EpisodeRow` /
 * `PartialSubmission` / `VerificationErrorRow`). This module turns a `ServiceRequest`
 * + linked `EpisodeOfCare` + associated `Patient` / `Organization` / `Practitioner`
 * resources into that row shape so the existing UI renders real data without per-cell
 * conditionals.
 *
 * All tracker-stage reading/writing uses `meta.tag`; see `serviceRequestFhirConstants.ts`.
 */

import type {
  Coverage,
  CoverageEligibilityResponse,
  EpisodeOfCare,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { therapyServiceTypeToBillingCode } from '@unify/fhir-model/resources/coverage-eligibility';
import { getPrimaryActiveCoverage } from '@unify/fhir-model/resources/coverage';
import { getPaymentTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { getPerformerRef } from '@unify/fhir-model/resources/service-request';
import type {
  EpisodeRow,
  EpisodeServiceType,
  LifecycleStage,
} from '../pages/episode-tracker/types/lifecycle';
import {
  readEpisodeOfCareId,
  readTrackerStage,
  THERAPY_SERVICE_TYPE_CS_SYSTEM,
  THERAPY_SERVICE_TYPE_LEGACY_SYSTEM,
  TRACKER_STAGE_DISPLAY,
  type TrackerStageCode,
} from './serviceRequestFhirConstants';
import {
  getRequestedGroupTemplateRef,
} from './groupTherapy/requestedGroupTemplate';
import { therapyServiceTypeLabel } from './fhirValueSets/sessionAndEpisodeTypes';

// ---------------------------------------------------------------------------
// Lookup bag
// ---------------------------------------------------------------------------

/** Resources resolved by reference so we can populate display fields without extra queries. */
export interface TrackerRowLookup {
  patientsById: Map<string, Patient>;
  episodesById: Map<string, EpisodeOfCare>;
  organizationsById: Map<string, Organization>;
  practitionersById: Map<string, Practitioner>;
  /** PractitionerRoles keyed by Practitioner id — used to resolve a provider's practice. */
  practitionerRolesByPractitionerId: Map<string, PractitionerRole[]>;
  coveragesByPatientId?: Map<string, Coverage[]>;
  eligibilityResponsesByPatientId?: Map<string, CoverageEligibilityResponse[]>;
  /** Transfer context QRs keyed by EpisodeOfCare id. */
  transferQrsByEpisodeId?: Map<string, import('@medplum/fhirtypes').QuestionnaireResponse>;
  /** Booked high-risk screening call (date + provider) keyed by Patient id. */
  screeningCallByPatientId?: Map<string, { start?: string; providerName?: string }>;
}

export function emptyTrackerRowLookup(): TrackerRowLookup {
  return {
    patientsById: new Map(),
    episodesById: new Map(),
    organizationsById: new Map(),
    practitionersById: new Map(),
    practitionerRolesByPractitionerId: new Map(),
    coveragesByPatientId: new Map(),
    eligibilityResponsesByPatientId: new Map(),
  };
}

export function buildTrackerRowLookup(opts: {
  patients?: Patient[];
  episodes?: EpisodeOfCare[];
  organizations?: Organization[];
  practitioners?: Practitioner[];
  practitionerRoles?: PractitionerRole[];
  coverages?: Coverage[];
  eligibilityResponses?: CoverageEligibilityResponse[];
}): TrackerRowLookup {
  const l = emptyTrackerRowLookup();
  for (const p of opts.patients ?? []) if (p.id) l.patientsById.set(p.id, p);
  for (const e of opts.episodes ?? []) if (e.id) l.episodesById.set(e.id, e);
  for (const o of opts.organizations ?? [])
    if (o.id) l.organizationsById.set(o.id, o);
  for (const pr of opts.practitioners ?? [])
    if (pr.id) l.practitionersById.set(pr.id, pr);
  for (const role of opts.practitionerRoles ?? []) {
    const pid = extractIdFromRef(role.practitioner?.reference, 'Practitioner');
    if (!pid) continue;
    const arr = l.practitionerRolesByPractitionerId.get(pid) ?? [];
    arr.push(role);
    l.practitionerRolesByPractitionerId.set(pid, arr);
  }
  const coverageMap = l.coveragesByPatientId!;
  for (const c of opts.coverages ?? []) {
    const ref = c.beneficiary?.reference ?? c.subscriber?.reference;
    const id = extractIdFromRef(ref, 'Patient');
    if (!id) continue;
    const arr = coverageMap.get(id) ?? [];
    arr.push(c);
    coverageMap.set(id, arr);
  }
  const cerMap = l.eligibilityResponsesByPatientId!;
  for (const cer of opts.eligibilityResponses ?? []) {
    const id = extractIdFromRef(cer.patient?.reference, 'Patient');
    if (!id) continue;
    const arr = cerMap.get(id) ?? [];
    arr.push(cer);
    cerMap.set(id, arr);
  }
  return l;
}

// ---------------------------------------------------------------------------
// Stage conversions (PRD tracker stage ↔ legacy UI LifecycleStage)
// ---------------------------------------------------------------------------

/**
 * The tracker UI also has `transfer`, `active`, and `all` stages that are **not** tracker
 * tag codes per the PRD. The PRD tag codes map 1:1 to the remaining UI stages.
 */
export function trackerStageToLifecycleStage(
  stage: TrackerStageCode,
): LifecycleStage {
  return stage as LifecycleStage;
}

export function lifecycleStageToTrackerStage(
  stage: LifecycleStage,
): TrackerStageCode | undefined {
  switch (stage) {
    case 'new-client':
    case 'needs-verification':
    case 'waitlist':
    case 'screening':
    case 'matching':
    case 'consultation':
    case 'fulfilled':
    case 'inactive':
    case 'cancelled':
    case 'transfer':
      return stage;
    case 'active':
    case 'all':
      return undefined;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractIdFromRef(
  ref: string | undefined,
  resourceType: string,
): string | undefined {
  if (!ref) return undefined;
  const prefix = `${resourceType}/`;
  return ref.startsWith(prefix) ? ref.slice(prefix.length) : undefined;
}

function practitionerDisplayName(pr?: Practitioner): string | undefined {
  if (!pr) return undefined;
  const n = pr.name?.[0];
  if (!n) return pr.id;
  return [n.prefix?.[0], ...(n.given ?? []), n.family].filter(Boolean).join(' ').trim() || pr.id;
}

function patientDisplayName(p?: Patient): string | undefined {
  if (!p) return undefined;
  const n = p.name?.[0];
  if (!n) return p.id;
  const given = (n.given ?? []).join(' ');
  return [given, n.family].filter(Boolean).join(' ').trim() || p.id;
}

function daysBetween(fromIso?: string, toIso?: string): number | undefined {
  if (!fromIso) return undefined;
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  if (Number.isNaN(from) || Number.isNaN(to)) return undefined;
  return Math.max(0, Math.floor((to - from) / (24 * 60 * 60 * 1000)));
}

function addressState(p?: Patient): string | undefined {
  return p?.address?.[0]?.state;
}

/** Extract a therapy-service-type code from `ServiceRequest.code` (PRD CodeSystem preferred). */
export function extractServiceTypeCode(
  sr: Pick<ServiceRequest, 'code'>,
): string | undefined {
  const coding = sr.code?.coding ?? [];
  const match =
    coding.find((c) => c.system === THERAPY_SERVICE_TYPE_CS_SYSTEM) ??
    coding.find((c) => c.system === THERAPY_SERVICE_TYPE_LEGACY_SYSTEM);
  const code = match?.code;
  if (!code) return undefined;
  return code;
}

function serviceTypeDisplay(
  sr: Pick<ServiceRequest, 'code'>,
): string {
  const code = extractServiceTypeCode(sr);
  if (code) return therapyServiceTypeLabel(code);
  const cc = sr.code;
  if (cc?.text?.trim()) return cc.text.trim();
  const display = cc?.coding?.[0]?.display?.trim();
  if (display) return display;
  const fallbackCode = cc?.coding?.[0]?.code;
  if (fallbackCode) return therapyServiceTypeLabel(fallbackCode);
  return 'Therapy';
}

function asEpisodeServiceType(display: string): EpisodeServiceType {
  const known: EpisodeServiceType[] = [
    'Individual Therapy',
    'Couples Therapy',
    'Family Therapy',
    'IOP',
    'Group Therapy',
    'Child & Adolescent Therapy',
    'Medication Management',
  ];
  if (display === 'Intensive Outpatient Program') return 'IOP';
  if ((known as string[]).includes(display)) return display as EpisodeServiceType;
  return display as EpisodeServiceType;
}

function resolveCoverageForServiceRequest(
  eoc: EpisodeOfCare | undefined,
  lookup: TrackerRowLookup,
  patientId?: string,
): Coverage | undefined {
  if (!patientId || !eoc) return undefined;
  if (getPaymentTypeCode(eoc) !== 'insurance') return undefined;
  const covs = lookup.coveragesByPatientId?.get(patientId) ?? [];
  return getPrimaryActiveCoverage(covs);
}

function readMemberId(coverage?: Coverage): string | undefined {
  if (!coverage) return undefined;
  return (
    coverage.identifier?.find((i) => i.type?.text === 'Member ID')?.value
    ?? coverage.subscriberId
  );
}

function readPayerDisplay(coverage?: Coverage): string | undefined {
  return coverage?.payor?.[0]?.display ?? coverage?.payor?.[0]?.reference;
}

function cersForCoverage(
  lookup: TrackerRowLookup,
  patientId: string | undefined,
  coverageRef: string | undefined,
): CoverageEligibilityResponse[] {
  if (!patientId || !coverageRef) return [];
  const all = lookup.eligibilityResponsesByPatientId?.get(patientId) ?? [];
  return all.filter((r) => r.insurance?.[0]?.coverage?.reference === coverageRef);
}

function latestCer(
  cers: CoverageEligibilityResponse[],
  outcome?: CoverageEligibilityResponse['outcome'],
): CoverageEligibilityResponse | undefined {
  const filtered = outcome ? cers.filter((r) => r.outcome === outcome) : cers;
  return filtered.sort((a, b) => {
    const aMs = Date.parse(a.created ?? '');
    const bMs = Date.parse(b.created ?? '');
    return (Number.isNaN(bMs) ? 0 : bMs) - (Number.isNaN(aMs) ? 0 : aMs);
  })[0];
}

function formatCopayFromCer(cer?: CoverageEligibilityResponse): string | undefined {
  if (!cer) return undefined;
  for (const ins of cer.insurance ?? []) {
    for (const item of ins.item ?? []) {
      const copay = item.benefit?.find((b) =>
        b.type?.coding?.some((c) => c.code === 'copay'),
      );
      const value = copay?.allowedMoney?.value;
      if (value !== undefined) return `$${value}`;
    }
  }
  return undefined;
}

function reqOrgDisplay(
  lookup: TrackerRowLookup,
  managingOrgRef?: Reference,
): string | undefined {
  if (managingOrgRef?.display) return managingOrgRef.display;
  const id = extractIdFromRef(managingOrgRef?.reference, 'Organization');
  if (!id) return undefined;
  return lookup.organizationsById.get(id)?.name;
}

function careManagerDisplay(
  lookup: TrackerRowLookup,
  eoc?: EpisodeOfCare,
): string | undefined {
  const cm = eoc?.careManager;
  if (cm?.display) return cm.display;
  const id = extractIdFromRef(cm?.reference, 'Practitioner');
  if (!id) return undefined;
  return practitionerDisplayName(lookup.practitionersById.get(id));
}

/** Practice the assigned provider (EoC.careManager) belongs to, via PractitionerRole.organization. */
function providerPracticeDisplay(
  lookup: TrackerRowLookup,
  eoc?: EpisodeOfCare,
): string | undefined {
  const id = extractIdFromRef(eoc?.careManager?.reference, 'Practitioner');
  if (!id) return undefined;
  const roles = lookup.practitionerRolesByPractitionerId.get(id) ?? [];
  for (const role of roles) {
    const name = reqOrgDisplay(lookup, role.organization);
    if (name) return name;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Tracker row mapping
// ---------------------------------------------------------------------------

/**
 * Convert a `ServiceRequest` (+ resolved dependencies) to a tracker `EpisodeRow`.
 *
 * Fields the UI expects are populated best-effort from the PRD mapping. Missing data
 * is left undefined (the existing UI already handles empty cells).
 */
export function serviceRequestToTrackerRow(
  sr: ServiceRequest,
  lookup: TrackerRowLookup,
): EpisodeRow {
  const stage = readTrackerStage(sr.meta);
  const subjectId = extractIdFromRef(sr.subject?.reference, 'Patient');
  const patient = subjectId ? lookup.patientsById.get(subjectId) : undefined;

  const eocId = readEpisodeOfCareId(sr);
  const eoc = eocId ? lookup.episodesById.get(eocId) : undefined;

  const serviceTypeDisp = serviceTypeDisplay(sr);
  const episodeServiceType = asEpisodeServiceType(serviceTypeDisp);

  const patientName =
    patientDisplayName(patient) ?? sr.subject?.display ?? 'Client';
  const submissionIso = sr.authoredOn;
  const assignedProviderName = careManagerDisplay(lookup, eoc);
  const performerRef = getPerformerRef(sr);
  const practiceName =
    reqOrgDisplay(lookup, performerRef)
    ?? providerPracticeDisplay(lookup, eoc)
    ?? reqOrgDisplay(lookup, eoc?.managingOrganization);
  const coverage = resolveCoverageForServiceRequest(eoc, lookup, subjectId);
  const coverageRef = coverage?.id ? `Coverage/${coverage.id}` : undefined;
  const payerName = readPayerDisplay(coverage);
  const memberId = readMemberId(coverage);
  const therapyCode = extractServiceTypeCode(sr);
  const billingCode = therapyServiceTypeToBillingCode(therapyCode);
  const serviceTypeCodes = billingCode ? [billingCode] : undefined;

  const coverageCers = cersForCoverage(lookup, subjectId, coverageRef);
  const latestErrorCer = latestCer(coverageCers, 'error');
  const latestCompleteCer = latestCer(coverageCers, 'complete');
  const soharErrorReason = latestErrorCer?.disposition;
  const assumedCopay = formatCopayFromCer(latestCompleteCer);

  // Transfer-specific fields from QR
  let fromProvider: string | undefined;
  let transferReason: string | undefined;
  let transferRequestedAt: string | undefined;
  let transferStatus: string | undefined;

  if ((stage === 'transfer' || stage === 'consultation') && eocId) {
    const qr = lookup.transferQrsByEpisodeId?.get(eocId);
    if (qr) {
      fromProvider = qr.author?.display;
      transferRequestedAt = qr.authored?.slice(0, 10);
      const reasonItems = qr.item?.find((i) => i.linkId === 'transfer-reasons');
      if (reasonItems?.answer?.length) {
        transferReason = reasonItems.answer
          .map((a) => a.valueString)
          .filter(Boolean)
          .join(', ');
      }
    }
    transferStatus = stage === 'transfer' ? 'Pending Transfer' : 'Pending Consultation';
  }

  // Screening-specific: booked screening-call appointment (Appointment Date + Screening Provider).
  const screeningCall =
    stage === 'screening' && subjectId
      ? lookup.screeningCallByPatientId?.get(subjectId)
      : undefined;

  const row: EpisodeRow = {
    id: sr.id ?? '',
    ...(eocId ? { episodeOfCareId: eocId } : {}),
    clientId: subjectId ?? '',
    clientName: patientName,
    episodeServiceType,
    stage: (stage as LifecycleStage) ?? 'all',
    practice: practiceName,
    payer: payerName,
    memberId,
    serviceTypeCodes,
    soharErrorReason,
    assumedCopay,
    ...(coverage?.id ? { coverageId: coverage.id } : {}),
    assignedProvider: assignedProviderName,
    state: addressState(patient),
    submissionDate: submissionIso || undefined,
    email: patient?.telecom?.find((t) => t.system === 'email')?.value,
    phone: patient?.telecom?.find((t) => t.system === 'phone')?.value,
    daysWaiting: daysBetween(submissionIso),
    requestedGroupTemplate:
      getRequestedGroupTemplateRef(sr)?.reference
      ?? getRequestedGroupTemplateRef(sr)?.display,
    startDate: eoc?.period?.start?.slice(0, 10),
    fulfilledDate:
      stage === 'fulfilled'
        ? eoc?.period?.start?.slice(0, 10) ?? submissionIso?.slice(0, 10)
        : undefined,
    fulfilledStatus:
      stage === 'fulfilled'
        ? eoc?.status === 'finished'
          ? 'completed'
          : 'ongoing'
        : undefined,
    cancelledDate:
      stage === 'cancelled'
        ? eoc?.period?.end?.slice(0, 10) ?? sr.meta?.lastUpdated?.slice(0, 10)
        : undefined,
    deactivationDate:
      stage === 'inactive'
        ? eoc?.period?.end?.slice(0, 10) ?? sr.meta?.lastUpdated?.slice(0, 10)
        : undefined,
    fromProvider,
    transferReason,
    transferRequestedAt,
    transferStatus,
    ...(stage === 'screening' ? { daysInScreening: daysBetween(submissionIso) } : {}),
    ...(screeningCall?.start ? { screeningAppointmentDate: screeningCall.start } : {}),
    ...(screeningCall?.providerName ? { screeningProvider: screeningCall.providerName } : {}),
    ...(screeningCall ? { hasScreeningCall: true } : {}),
  };
  return row;
}

// ---------------------------------------------------------------------------
// Builders for new ServiceRequest / EpisodeOfCare pairs
// ---------------------------------------------------------------------------

/** Matches `ServiceRequest.requester` (FHIR R4 allowed reference targets). */
export type ServiceRequestRequesterReference = NonNullable<ServiceRequest['requester']>;

export type EpisodeOfCareManagingOrganizationReference = NonNullable<
  EpisodeOfCare['managingOrganization']
>;
export type EpisodeOfCareCareManagerReference = NonNullable<EpisodeOfCare['careManager']>;

export interface BuildServiceRequestInput {
  patient: Patient;
  therapyTypeCode: string;
  stage: TrackerStageCode;
  /** Who originated the request. Client, provider, or admin/org. */
  requester: ServiceRequestRequesterReference;
  /** Optional existing EoC id (set after the pair is written). */
  episodeOfCareId?: string;
  /** Optional additional meta tags (e.g. RBAC pilot). */
  additionalTags?: { system: string; code: string; display?: string }[];
  /** Optional free-text admin/provider/client note. */
  initialNote?: string;
  /** Optional ISO timestamp; defaults to `new Date().toISOString()`. */
  authoredOn?: string;
  /** Optional priority. Defaults to `routine`. */
  priority?: 'routine' | 'urgent' | 'asap' | 'stat';
  /** Practice Organization — canonical org scope for this episode (ServiceRequest.performer). */
  performerOrganization?: EpisodeOfCareManagingOrganizationReference;
}

export interface BuildEpisodeOfCareInput {
  patient: Patient;
  therapyTypeCode: string;
  /** `planned` at intake; `active` on fulfillment; `onhold` inactive; `cancelled` cancelled. */
  status?: EpisodeOfCare['status'];
  careManagerReference?: EpisodeOfCareCareManagerReference;
  managingOrganizationReference?: EpisodeOfCareManagingOrganizationReference;
  periodStart?: string;
  additionalTags?: { system: string; code: string; display?: string }[];
}

export const DEFAULT_SERVICE_REQUEST_STAGE: TrackerStageCode = 'new-client';

export function applyStageToStatus(stage: TrackerStageCode): {
  /** Waitlist uses `on-hold` per Group Therapy PRD; all other stages stay `active`. */
  serviceRequestStatus: 'active' | 'on-hold';
  episodeOfCareStatus: EpisodeOfCare['status'];
} {
  let eocStatus: EpisodeOfCare['status'] = 'planned';
  let serviceRequestStatus: 'active' | 'on-hold' = 'active';
  switch (stage) {
    case 'waitlist':
      eocStatus = 'waitlist';
      serviceRequestStatus = 'on-hold';
      break;
    case 'fulfilled':
      eocStatus = 'active';
      break;
    case 'inactive':
      eocStatus = 'onhold';
      break;
    case 'cancelled':
      eocStatus = 'cancelled';
      break;
    default:
      eocStatus = 'planned';
      break;
  }
  return { serviceRequestStatus, episodeOfCareStatus: eocStatus };
}

/** Human label for tracker stage (used for display in meta.tag writes). */
export function trackerStageLabel(stage: TrackerStageCode): string {
  return TRACKER_STAGE_DISPLAY[stage];
}
